import type { Persona } from './personas';

/**
 * Persona tile icon used across the resting / compressed / active
 * layers of `WhoItsForSection`. Renders the persona's brand-tinted
 * background tile + inline-SVG glyph. Inline SVG (not lucide-react)
 * keeps consistency with the rest of the codebase's icon system and
 * avoids adding an icon library to the bundle. Marked `aria-hidden`
 * because the persona title text already conveys meaning.
 */
export function PersonaIcon({ persona }: { readonly persona: Persona }) {
  return (
    <span
      aria-hidden="true"
      className={`flex h-14 w-14 items-center justify-center rounded-md border ${persona.iconBgClass}`}
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        strokeWidth="1.5"
        strokeLinecap="round"
        className={persona.iconStrokeClass}
      >
        {persona.icon}
      </svg>
    </span>
  );
}

/**
 * Small magnifier-glass affordance shown next to each persona title in
 * the resting (no-hover) state. Hints to users that the card is
 * interactive — hovering or focusing it reveals the description + the
 * reflection quote. Same inline-SVG approach as `PersonaIcon` to stay
 * consistent with the codebase's icon system (no lucide-react
 * dependency in the bundle). Marked `aria-hidden` because the title
 * text already conveys meaning.
 */
export function MagnifierHint() {
  return (
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-text-subtle/60"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
